/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    ICheckoutCustomerAccountPaymentViewProps,
    IAccountPaymentEditViewForm,
    IAccountPaymentSummaryViewForm
} from '@msdyn365-commerce-modules/checkout';
import { ICheckoutCustomerAccountPaymentProps as ICheckoutCustomerAccountPaymentExtensionProps } from '../definition-extensions/checkout-customer-account-payment.ext.props.autogenerated';
import MsDyn365 from '@msdyn365-commerce/core';
import { PaymentContext as PaymentContainerContext } from '../../../modules/payment-container/context/payment-context';
import { IPaymentTenderType, PaymentContext } from '../../../utils/paymentContext';

export const SummaryForm: React.FC<IAccountPaymentSummaryViewForm> = ({
    formProps,
    label,
    paymentAmount,
    addPaymentButton,
    appliedLine,
    bottomBorder
}) => (
    <Node {...formProps}>
        <>
            {label}
            {appliedLine}
            {bottomBorder}
        </>
    </Node>
);

export const EditForm: React.FC<IAccountPaymentEditViewForm> = (
    {
        formProps,
        inputLabel,
        inputAmount,
        customerName,
        customerAccountNumber,
        addPaymentButton,
        customerSince,
        accountCredit,
        alert,
        accountDetails,
        appliedLine,
        bottomBorder
    },
    props
) => {
    return (
        <Node {...formProps}>
            <>
                {customerName}
                {customerAccountNumber}
                {customerSince}
                {/* {accountCredit} */}
                {/* {accountDetails} */}
                {/* {inputLabel} */}
                {getInputLabel()}
                {/* {alert} */}
                {appliedLine}
                {bottomBorder}
            </>
        </Node>
    );
};

let customInputLabel = '';

const getInputLabel = () => {
    return (
        <span id='ms-checkout-customer-account__label' className='ms-checkout-customer-account__input-label'>
            {customInputLabel}
        </span>
    );
};

const addPayment = async (props: ICheckoutCustomerAccountPaymentViewProps): Promise<void> => {
    const checkoutState = props.data.checkout.result;

    if (!checkoutState) {
        props.context.telemetry.error('checkout state not found');
        return;
    }

    const cartTotal = (await props.data.checkout).checkoutCart.cart.TotalAmount;

    if (cartTotal) {
        await checkoutState.updateCustomerAccountAmount({ newAmount: cartTotal });
        props.context.telemetry.information('customer account payment amount updated');
    }
};

const CheckoutCustomerAccountView: React.FC<ICheckoutCustomerAccountPaymentViewProps &
    ICheckoutCustomerAccountPaymentExtensionProps<{}>> = props => {
    const {
        checkoutCustomerAccount,
        summaryView,
        editView,
        moduleState,
        id,
        config: { tenderType },
        context
    } = props;

    const shouldEnableSCA: boolean = context.app.config.shouldEnableSinglePaymentAuthorizationCheckout ?? false;
    const paymentContainerContext = React.useContext(PaymentContainerContext);
    const utilsPaymentContext = React.useContext(PaymentContext);

    const paymentContext = shouldEnableSCA ? utilsPaymentContext : paymentContainerContext;
    const paymentTenderTypes: IPaymentTenderType[] = [];

    React.useEffect(() => {
        tenderType?.map(tt => {
            paymentTenderTypes.push({
                tenderTypeId: tt.tenderId,
                tenderName: tt.tenderName,
                showOnNormalPartsCheckout: tt.showOnNormalPartsCheckout ?? true,
                showOnInvoiceCheckout: tt.showOnInvoiceCheckout ?? true
            });
        });
        paymentContext.registerPaymentMethod(id, paymentTenderTypes);
        paymentContext.reportIframeReady(id);
        sessionStorage.removeItem('ON_ACCOUNT_APPLIED');
        customInputLabel = props.config.processedOnAccountText || '';
    }, []);

    if (MsDyn365.isBrowser) {
        document.querySelector('.payment-selector__button-container--on-account')?.addEventListener('click', () => {
            if (sessionStorage.getItem('ON_ACCOUNT_APPLIED') === '1') {
                return;
            }
            sessionStorage.setItem('ON_ACCOUNT_APPLIED', '1');
            addPayment(props);
        });
    }

    return (
        <Module {...checkoutCustomerAccount}>
            {moduleState.isReady && summaryView && <SummaryForm {...summaryView} />}
            {!moduleState.isReady && editView && <EditForm {...editView} />}
        </Module>
    );
};

export default CheckoutCustomerAccountView;
